import { sortJobsByDisplayName } from "../../utils/jobs";
import type { BulkNurseJobsAction, BulkNurseJobsBlockedMode, BulkNurseJobsPreview, BulkNurseJobsRequest, Job, TeamNurse } from "../../utils/models";
import type { NamedGroupItem } from "../UIToolkit/molecules/NamedGroupCard";

export type BulkJobStepId = "choose" | "review";

/** What the Choose step has picked so far — each mode keeps its own picks, so toggling back loses nothing. */
export type BulkJobPicks = {
	action: BulkNurseJobsAction;
	addJobId: string | undefined;
	fromJobId: string | undefined;
	toJobId: string | undefined;
};

export const INITIAL_PICKS: BulkJobPicks = { action: "add", addJobId: undefined, fromJobId: undefined, toJobId: undefined };

/** The request the picks describe, or `null` while they are incomplete (the Continue button stays disabled). */
export const toBulkJobsRequest = (picks: BulkJobPicks, nurseIds: number[]): BulkNurseJobsRequest | null => {
	if (picks.action === "add") {
		return picks.addJobId ? { action: "add", fromJobId: null, toJobId: picks.addJobId, nurseIds } : null;
	}
	if (!picks.fromJobId || !picks.toJobId || picks.fromJobId === picks.toJobId) return null;
	return { action: "replace", fromJobId: picks.fromJobId, toJobId: picks.toJobId, nurseIds };
};

/**
 * Every job at least one selected employee holds — the only jobs "Replace" can meaningfully offer.
 *
 * Resolved against the department's full catalog, not its enabled jobs: a job the department has since
 * stopped offering is exactly the one an admin most wants to move people off. An id with no catalog
 * entry can't be named, so it can't be offered either.
 */
export const jobsHeldBy = (nurses: TeamNurse[], catalog: Job[]): Job[] => {
	// DEFAULT: `jobIds` is optional on older roster payloads; an employee without it holds nothing to replace.
	const heldIds = new Set(nurses.flatMap((nurse) => nurse.jobIds ?? []));
	return sortJobsByDisplayName(catalog.filter((job) => heldIds.has(job.id)));
};

/**
 * Ids from the server → names from the selection. The preview only ever returns ids the dialog sent,
 * so an id missing from the selection means the two disagree — dropped rather than shown as a number
 * nobody can act on, and sorted so a long list reads alphabetically.
 */
export const toNamedItems = (ids: number[], nameById: Map<number, string>): NamedGroupItem[] =>
	ids
		.flatMap((id) => {
			const label = nameById.get(id);
			return label === undefined ? [] : [{ id, label }];
		})
		.sort((a, b) => a.label.localeCompare(b.label));

/** Everyone the apply would change: the clear ones, plus the blocked ones if their shifts move too. */
export const countWillChange = (preview: BulkNurseJobsPreview, blockedMode: BulkNurseJobsBlockedMode): number =>
	preview.willChange.length + (blockedMode === "move_shifts" ? preview.blockedByShifts.length : 0);

export type ReviewPrimaryKey =
	| "team.bulk.jobs.confirm.add"
	| "team.bulk.jobs.confirm.replace"
	| "team.bulk.jobs.confirm.move"
	| "team.bulk.jobs.confirm.others_only";

/**
 * The primary button names what it will actually do. With blocked employees in play it names the
 * choice made for them, since that is the consequential half of the click.
 */
export const reviewPrimaryKey = (action: BulkNurseJobsAction, preview: BulkNurseJobsPreview, blockedMode: BulkNurseJobsBlockedMode): ReviewPrimaryKey => {
	if (action === "add") return "team.bulk.jobs.confirm.add";
	if (preview.blockedByShifts.length === 0) return "team.bulk.jobs.confirm.replace";
	return blockedMode === "move_shifts" ? "team.bulk.jobs.confirm.move" : "team.bulk.jobs.confirm.others_only";
};
